import React, { forwardRef } from 'react'
import styled, { css } from 'styled-components'

import WithForcedMonospace from './WithForcedMonospace'
import WithPodiumStyles from './WithPodiumStyles'
import WithAnimatedDeltas from './WithAnimatedDeltas'
import { SafeFlagImg } from './SafeImgs'
import CircleSVG from '../svgs/CircleSVG'
import CirclePlayIconSVG from '../svgs/CirclePlayIconSVG'

import { ZERO_WIDTH_SPACE, NO_INFORMATION_SYMBOL } from '../unicodeChars'
import { trimMs, CHAPTER_COMPLETE, CHAPTER_IN_PROGRESS, CHAPTER_NOT_STARTED } from '../utils'

import BingoPointsCell from './BingoPointsCell'
import PlayerTotalAnimatedDelta from './PlayerTotalAnimatedDelta'
import FlashDiv from './FlashDiv'

import { useStore } from '../zustandStore'

const DetailedBoardRow = forwardRef(({
  player,
  rankOverrideKey = null,
  chapterNames,
  includeTotal = true,
  className,
  onClick,
  onDoubleClick,
  onMouseEnter,
  requireDoubleClick,
  isFeatured,
  isLive = false,
  isPlaceholder,
  shouldFlash,
  isLoading,
  loadingDiv,
  children,
  withAnimatedTotalDelta,
  animatedTotalDeltaProps = {},
  omitDeltaForChapterNames,
  includeRankForChapters = [],
  msDecimals = 3,
  showMsForTotal,
  nameColumnWidthPercent = '17.5',
  chapterTimeSpacerStrings,
  totalTimeSpacerString,
}, ref) => {
  let clickHandlerProps = isLoading
    ? {}
    : requireDoubleClick
      ? { onDoubleClick: onClick }
      : { onClick: onClick, onDoubleClick: onDoubleClick}
      // : { onDoubleClick: onDoubleClick }

  const {
    defaultChapterNames,
    isBingo,
    isConcluded,
    chapters
  } = useStore(state => ({
    defaultChapterNames: state.event.chapterNames,
    isBingo: state.event.isBingo,
    isConcluded: state.event.isConcluded,
    chapters: state.event.chapters
  }))

  if (chapterNames == null || chapterNames.length === 0) {
    chapterNames = defaultChapterNames
  }

  if (includeRankForChapters.length > 0) {
    // default value for chapterTimespacerstrings to be safe
    chapterTimeSpacerStrings ||= Array(includeRankForChapters.length).fill('')
  }
  chapterTimeSpacerStrings ||= Array(chapterNames.length).fill('')

  const rankOverride = rankOverrideKey ? player[rankOverrideKey] : null

  return (
    <Row ref={ref} chapterCount={chapterNames.length} title={player.name} includeTotal={includeTotal} nameColumnWidthPercent={nameColumnWidthPercent} isLoading={isLoading} onMouseEnter={onMouseEnter} {...clickHandlerProps} className={className + ' short-transition'}>
      {shouldFlash && <FlashDiv />}
      {isLoading && loadingDiv}
      <PlayerColumn className='column-player' isLive={isLive}>
        <Rank className='rank'>
          {!isPlaceholder && (player.completedAllChapters || (isBingo && player.completedAnyChapters)) &&
            <WithForcedMonospace>
              {rankOverride || player.rank}
            </WithForcedMonospace>
          }
        </Rank>
        <Name className='name'>
          {!isPlaceholder
            ? (<>
              {/* {isFeatured && <span className='featured'>*</span>} */}
              {player.name}
            </>)
            : (children || ZERO_WIDTH_SPACE)
          }
        </Name>
        {!isPlaceholder && player.isCountryPresent && (
          <Flag className='flag'>
            <SafeFlagImg {...player} />
          </Flag>
        )}
        {!isPlaceholder && isLive
          ? isFeatured
            ? <CirclePlayIconSVG visible={isLive} className='live-icon' />
            : <CircleSVG visible={isLive} className='live-icon' />
          : null
        }
      </PlayerColumn>

      {!isPlaceholder && (<>
        {chapterNames.map((chapterName, i) => {
          const shouldIncludeRankForChapter = includeRankForChapters.includes(chapterName)
          const chapterTimeSpacerString = chapterTimeSpacerStrings[i]

          let shouldDisplayBestTime = player.eventBestTimes[chapterName]
          let shouldDisplayBingoPoints = false

          let bingoChapterStates = {}
          if (isBingo && !isConcluded) {
            bingoChapterStates = player.bingoChapterStates

            switch(bingoChapterStates[chapterName]) {
              case CHAPTER_COMPLETE:
                shouldDisplayBestTime = true
                shouldDisplayBingoPoints = false
                break
              case CHAPTER_IN_PROGRESS:
                shouldDisplayBestTime = false
                shouldDisplayBingoPoints = true
                break 
              case CHAPTER_NOT_STARTED:
                shouldDisplayBestTime = false
                shouldDisplayBingoPoints = false
                break
            }
          }

          return (
            <ChapterTimeColumn className='column column-chapter' col={i + 2} key={chapterName}>
              {shouldIncludeRankForChapter && (
                <ChapterRank className='chapter-rank'>
                  <WithForcedMonospace>
                    {player.eventBestTimes[chapterName] ? player.eventChapterRanks[chapterName] : null}
                  </WithForcedMonospace>
                </ChapterRank>
              )}
              {shouldDisplayBestTime ? (
                <GridWrapper>
                  <GridCell className='visible-chapter-time' textAlign={chapterTimeSpacerString && 'right'}>
                    <WithAnimatedDeltas
                      delta={player.eventChapterDeltas[chapterName]}
                      updatedAt={player.eventBestTimesUpdatedAt[chapterName]}
                      bestTime={player.eventBestTimes[chapterName]}
                    >
                      <WithPodiumStyles rank={player.eventChapterRanks[chapterName]}>
                        {trimMs(player.eventBestTimes[chapterName], msDecimals)}
                      </WithPodiumStyles>
                    </WithAnimatedDeltas>
                  </GridCell>
                  {/* if timeSpacerString is provided, it's rendered here */}
                  {chapterTimeSpacerString && (
                    <GridCell invisible>
                      <WithForcedMonospace>
                        {trimMs(chapterTimeSpacerString, msDecimals)}
                      </WithForcedMonospace>
                    </GridCell>
                  )}
                </GridWrapper> 
              ) : shouldDisplayBingoPoints ? (
                <BingoPointsCell
                  points={player.chapterBingoPoints[chapterName]}
                  maxPoints={chapters[chapterName].bingoMaxPoints}
                />
              ) : (
                <GridWrapper>
                  <GridCell>
                    {NO_INFORMATION_SYMBOL}
                  </GridCell>
                  <GridCell invisible>
                    <WithForcedMonospace>
                      {trimMs(chapterTimeSpacerString || '0:00.000', msDecimals)}
                    </WithForcedMonospace>
                  </GridCell>
                </GridWrapper> 
              )}
            </ChapterTimeColumn>
          )
        })}

        {includeTotal &&
          <TotalColumn chapterCount={chapterNames.length} className='column column-total'>
            {player.completedAllChapters || (isBingo && player.completedAnyChapters)
              ? (withAnimatedTotalDelta
                ? (
                  <StyledTotalWithDelta
                    deltasClassName='display-flex'
                    showFinishedChapterCount={false}
                    player={player}
                    omitDeltaForChapterNames={omitDeltaForChapterNames}
                    animatedDeltaProps={animatedTotalDeltaProps}
                    msDecimals={msDecimals}
                    timeSpacerString={totalTimeSpacerString || ''}
                  />
                ) : (
                  <GridWrapper >
                    {/* somehow this wrapper span allows the small decimals to vertically align IDK MAN */}
                    <GridCell textAlign={totalTimeSpacerString && 'right'}>
                      <NoWrapSpan>
                        <WithForcedMonospace>
                          {showMsForTotal
                            ? player.eventBestTimes.total                      
                            : trimMs(player.eventBestTimes.total, msDecimals)
                          }
                        </WithForcedMonospace>
                      </NoWrapSpan>
                    </GridCell>
                    <GridCell invisible>
                      <WithForcedMonospace>
                        {totalTimeSpacerString || (
                          showMsForTotal ? '00:00.000' : trimMs('00:00.000', msDecimals)
                        )}
                      </WithForcedMonospace>
                    </GridCell>
                  </GridWrapper>
                )
              ) : (
                <GridWrapper>
                  <GridCell>
                    {NO_INFORMATION_SYMBOL}
                  </GridCell>
                  <GridCell invisible>
                    <WithForcedMonospace>
                      {totalTimeSpacerString || (
                        showMsForTotal ? '00:00.000' : trimMs('00:00.000', msDecimals)
                      )}
                    </WithForcedMonospace>
                  </GridCell>
                </GridWrapper>
              )
            }
          </TotalColumn>
        }
      </>)}
    </Row>
  )
})

// Width of the columns is determined entirely by the parent rendering component
// which is why the 'column' and 'column-player' CSS classes are on some components above
// and also why the styled-components CSS blocks below don't mention width

export default DetailedBoardRow

const Row = styled.div`
  position: relative;
  display: grid;

  grid-template-columns: ${props => props.nameColumnWidthPercent}% repeat(${props => props.chapterCount}, 10fr) ${props => props.includeTotal && '10fr'};
  > * {
    grid-row: 1;
  }

  &.featured {
    border-color: pink!important;
    border-width: 3px!important;
    margin-top: -1px;
    margin-bottom: -1px;
    border-radius: 2px;
  }

  ${props => props.isHighlighted && css`
    background-color: var(--highlighted-player-obs-color);
    .column-chapter-progress, .column-sum-of-best {
      color: var(--total-color);
    }
  `}

  .live-icon {
    grid-row: 1;
    grid-column: 4;
    ${'' /* margin-left: auto;
    margin-top: 0.15em; */}
    margin-left: 0.2em;
    ${'' /* margin-right: 2%; */}
    align-self: center;
    line-height: 0;
    ${'' /* grid-column: 2; */}
    ${'' /* align-self: center; */}
    ${'' /* line-height: 0.2em; */}
  }

  .flip-move {
    ${'' /* margin-left: auto; */}
    ${'' /* margin-top: 0.15em; */}
    ${'' /* align-self: center; */}
    ${'' /* line-height: 0.2em; */}
  }
  ${props => !props.isLoading && css`
    &:hover {
      background-color: var(--row-hover-color)!important;
      &:not(.featured) {border-color: var(--row-hover-color)!important;}
    }
    &:active {
      background-color: var(--row-active-color)!important;
      &:not(.featured) {border-color: var(--row-active-color)!important;}
    }
  `}
`

const PlayerColumn = styled.div`
  grid-column: 1;
  display: grid;
  width: 100%;
  ${'' /* align-items: flex-end; */}
  ${'' /* margin-top: 0.1em;
  margin-bottom: 0.1em; */}
  ${'' /* font-size: 95%; */}
  grid-template-columns: min-content auto 1fr ${props => props.isLive ? 'min-content' : '0'};
  ${'' /* max-width: 200px; */}
`

const Rank = styled.div`
  grid-row: 1;
  grid-column: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 75%;
  margin: 0.2em 0.2em 0 0.25em;

  color: var(--rank-color);
  line-height: 1.45em;
  width: 1.04em;
  min-width: 1.04em;
`

const Name = styled.div`
  grid-row: 1;
  grid-column: 2;
  line-height: 1.29em;
  margin-right: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  .featured {
    color: yellow;
    ${'' /* font-size: 65%; */}
  }
`

const Flag = styled.div`

  height: 1.09em;
  ${'' /* padding-bottom: 0.04em; */}
  margin: auto 0;
  > img {
    height: 70%;
    margin-left: 0.1em;
    margin-bottom: 0.25em;
  }
  grid-row: 1;
  grid-column: 3;
`

const ChapterTimeColumn = styled.div`
  line-height: 1.29em;
  display: flex;
  justify-content: center;
  /* grid-column: 1; */
  grid-column: ${props => props.col};

  .bingo-points-cell-wrapper {
    font-size: 0.85em;
  }
`

const ChapterRank = styled.div`
  font-size: 0.75em;
  min-width: 1.02em;
  text-align: center;
  margin-right: 0.3em;
  color: var(--chapter-rank-color);
`

const TotalColumn = styled(ChapterTimeColumn)`
  color: var(--total-color);
  grid-column: ${props => props.chapterCount + 2};
  display: flex;
  align-items: center;
`

const StyledTotalWithDelta = styled(PlayerTotalAnimatedDelta)`
  ${'' /* grid-column: 3; */}
  ${'' /* max-width: 4em; */}
  ${'' /* min-width: 4em; */}
  line-height: 1.29em;
  display: flex;
  ${'' /* margin-right: 3em; */}
  ${'' /* justify-content: flex-end; */}
  ${'' /* align-items: center; */}
  .display-flex {
    display: flex;
    justify-content: flex-end;
  }

  .delta {
    /* text-transform: uppercase; */
    justify-content: flex-end;
  }
`

const NoWrapSpan = styled.span`
  white-space: nowrap;
`
const GridWrapper = styled.div`
  display: grid;
`
const GridCell = styled.div`
  grid-column: 1;
  grid-row: 1;
  text-align: ${props => props.textAlign || 'center'};
  white-space: nowrap;
  align-self: center;
  ${props => props.invisible && css`
    visibility: hidden;
    /* color: red;
    opacity: 0.5; */
  `}
`